import {
  getProductCountsByLocation,
  getProductSalesByLocation,
  getTotalUnusedCreditPoints,
  getCreditPointsStatistics,
} from '@apis/statistics';
import { CumulativeSalesChart } from '@components/dashboard/CumulativeSalesChart';
import MonthlyPaymentComparisonChart from '@components/dashboard/MonthlyPaymentComparisonChart';
import PaymentTimeSlotTrendsChart from '@components/dashboard/PaymentHeatmapChart';
import { useStatistics } from '@hooks/statistics';
import { DailyNetSales, DailyPointUsageCount } from '@models/statistics';
import { BACKGROUND_COLORS, CHART_CATEGORY_COLORS, CHART_CATEGORY_LABELS } from '@pages/Dashboard';
import { useQuery } from '@tanstack/react-query';
import { Card, DatePicker, Tooltip, Button, Table } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import ClipLoader from 'react-spinners/ClipLoader';
import * as XLSX from 'xlsx';
import '../../pages/Dashboard.scss';

export const PaymentAnalysisTab: React.FC = () => {
  const {
    statisticsStore,
    cumulativeSalesBaseDate,
    setCumulativeSalesBaseDate,
    isLoadingDailyRefunds,
    isLoadingDailyPayments,
    isLoadingDailyPointUsage,
    isLoadingDailyNetSales,
    isLoadingPaymentCounts,
    isLoadingPaymentRankNaverTop,
    isLoadingPaymentRankCommerceTop,
    isLoadingPaymentRankClassTop,
    isLoadinRefundRankSeller,
    isLoadinRefundRankProduct,
    isLoadingPaymentRankKakaoTop,
    isLoadingPaymentRankEzwelTop,
    isLoadingCumulativeHourlySales,
    isLoadingPaymentTimeSlotTrends,
    isLoadingMonthlyPaymentComparison,
    isLoadingBuyerRegionPaymentAmounts,
  } = useStatistics('payment');

  // 지역별 상품 수 데이터
  const { data: productCountsByLocation, isLoading: isLoadingProductCountsByLocation } = useQuery({
    queryKey: ['product-counts-by-location'],
    queryFn: getProductCountsByLocation,
  });

  // 지역별 판매건수 차트를 위한 날짜 상태
  const [salesStartDate, setSalesStartDate] = useState<string>(moment().subtract(6, 'months').format('YYYY-MM-DD'));
  const [salesEndDate, setSalesEndDate] = useState<string>(moment().format('YYYY-MM-DD'));

  // 지역별 판매건수 데이터
  const { data: productSalesByLocation, isLoading: isLoadingProductSalesByLocation } = useQuery({
    queryKey: ['product-sales-by-location', salesStartDate, salesEndDate],
    queryFn: () => getProductSalesByLocation(salesStartDate, salesEndDate),
    enabled: !!salesStartDate && !!salesEndDate,
  });

  // 전체 미사용 포인트 데이터
  const { data: totalUnusedCreditPoints, isLoading: isLoadingTotalUnusedCreditPoints } = useQuery({
    queryKey: ['total-unused-credit-points'],
    queryFn: getTotalUnusedCreditPoints,
  });

  // 포인트 보유 통계 데이터
  const { data: creditPointsStatistics, isLoading: isLoadingCreditPointsStatistics } = useQuery({
    queryKey: ['credit-points-statistics'],
    queryFn: getCreditPointsStatistics,
  });

  const sellerDiff =
    (statisticsStore.paymentCounts?.seller?.hasIncome || 0) -
    (statisticsStore.paymentCounts?.samePastPeriod?.seller?.hasIncome || 0);
  const tutorDiff =
    (statisticsStore.paymentCounts?.tutor?.hasIncome || 0) -
    (statisticsStore.paymentCounts?.samePastPeriod?.tutor?.hasIncome || 0);
  const commerceDiff =
    (statisticsStore.paymentCounts?.commerce || 0) - (statisticsStore.paymentCounts?.samePastPeriod?.commerce || 0);
  const classDiff =
    (statisticsStore.paymentCounts?.class || 0) - (statisticsStore.paymentCounts?.samePastPeriod?.class || 0);

  // 포인트 보유 상위 50명 테이블 컬럼 정의
  const creditPointsColumns = [
    {
      title: '순위',
      dataIndex: 'rank',
      key: 'rank',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      width: 100,
      render: (_id: string) => {
        return <Button onClick={() => navigator.clipboard.writeText(_id)}>ID 복사</Button>;
      },
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (name: string) => name || '-',
    },
    {
      title: '계정구분',
      ellipsis: true,
      render: (record: any) => {
        return record.services?.kakao
          ? '카카오'
          : record.services?.naver
            ? '네이버'
            : record.services?.apple
              ? '애플'
              : '이메일';
      },
    },
    {
      title: '보유 포인트',
      dataIndex: 'creditPoints',
      key: 'creditPoints',
      width: 100,
      align: 'right' as const,
      render: (points: number) => `${points?.toLocaleString() || 0}P`,
    },
  ];
  const paymentRankNaverTop = {
    labels: (statisticsStore.paymentRankNaverTop || []).map((item) => item.sellerName),
    datasets: [
      {
        data: (statisticsStore.paymentRankNaverTop || []).map((item) => item.amount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const paymentRankKakaoTop = {
    labels: (statisticsStore.paymentRankKakaoTop || []).map((item) => item.sellerName),
    datasets: [
      {
        data: (statisticsStore.paymentRankKakaoTop || []).map((item) => item.amount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const paymentRankEzwelTop = {
    labels: (statisticsStore.paymentRankEzwelTop || []).map((item) => item.sellerName),
    datasets: [
      {
        data: (statisticsStore.paymentRankEzwelTop || []).map((item) => item.amount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const paymentRankCommerceTop = {
    labels: (statisticsStore.paymentRankCommerceTop || []).map((item) => item.sellerName),
    datasets: [
      {
        data: (statisticsStore.paymentRankCommerceTop || []).map((item) => item.amount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const paymentRankClassTop = {
    labels: (statisticsStore.paymentRankClassTop || []).map((item) => item.sellerName),
    datasets: [
      {
        data: (statisticsStore.paymentRankClassTop || []).map((item) => item.amount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const refundRankSeller = {
    labels: (statisticsStore.refundRankSeller || []).map((item) => item.sellerName),
    datasets: [
      {
        data: (statisticsStore.refundRankSeller || []).map((item) => item.amount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const refundRankProduct = {
    labels: (statisticsStore.refundRankProduct || []).map((item) => item.productName),
    datasets: [
      {
        data: (statisticsStore.refundRankProduct || []).map((item) => item.amount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  // 지역별 상품 수 차트 데이터
  const productCountsByLocationData = {
    labels: (productCountsByLocation?.data || []).map((item) =>
      item.location.replace('특별자치시', '').replace('특별자치도', ''),
    ),
    datasets: [
      {
        label: '상품 수',
        data: (productCountsByLocation?.data || []).map((item) => item.count),
        backgroundColor: BACKGROUND_COLORS.slice(0, (productCountsByLocation?.data || []).length),
        borderWidth: 1,
        borderColor: BACKGROUND_COLORS.slice(0, (productCountsByLocation?.data || []).length),
      },
    ],
  };

  // 지역별 판매건수 차트 데이터 (판매 건수 순으로 정렬)
  const sortedProductSalesByLocation = (productSalesByLocation?.data || []).sort((a, b) => {
    const aTotalSales = a.monthlyData.reduce((sum, item) => sum + item.count, 0);
    const bTotalSales = b.monthlyData.reduce((sum, item) => sum + item.count, 0);
    return bTotalSales - aTotalSales; // 내림차순 정렬
  });

  // 모든 날짜를 통합하여 정렬
  const allDates = new Set<string>();
  sortedProductSalesByLocation.forEach((locationData) => {
    locationData.monthlyData.forEach((item) => {
      allDates.add(item.date);
    });
  });
  const sortedDates = Array.from(allDates).sort();

  const productSalesByLocationData = {
    labels: sortedDates,
    datasets: sortedProductSalesByLocation.map((locationData, index) => ({
      label: locationData.location.replace('특별자치시', '').replace('특별자치도', ''),
      data: sortedDates.map((date) => {
        const monthData = locationData.monthlyData.find((item) => item.date === date);
        return monthData ? monthData.count : 0;
      }),
      borderColor: BACKGROUND_COLORS[index % BACKGROUND_COLORS.length],
      backgroundColor: BACKGROUND_COLORS[index % BACKGROUND_COLORS.length] + '20',
      borderWidth: 2,
      fill: false,
    })),
  };

  // 엑셀 다운로드 함수
  const downloadProductSalesByLocationExcel = () => {
    if (!productSalesByLocation?.data) return;

    const workbook = XLSX.utils.book_new();

    // 판매건수 데이터 준비
    const salesCountData = [];
    const salesCountHeaders = ['지역'];
    sortedDates.forEach((date) => {
      salesCountHeaders.push(date);
    });
    salesCountHeaders.push('총계');
    salesCountData.push(salesCountHeaders);

    // 판매건수 데이터 추가
    sortedProductSalesByLocation.forEach((locationData) => {
      const row = [locationData.location.replace('특별자치시', '').replace('특별자치도', '')];
      let total = 0;

      sortedDates.forEach((date) => {
        const monthData = locationData.monthlyData.find((item) => item.date === date);
        const count = monthData ? monthData.count : 0;
        row.push(count.toString());
        total += count;
      });

      row.push(total.toString());
      salesCountData.push(row);
    });

    // 판매금액 데이터 준비
    const salesAmountData = [];
    const salesAmountHeaders = ['지역'];
    sortedDates.forEach((date) => {
      salesAmountHeaders.push(date);
    });
    salesAmountHeaders.push('총계');
    salesAmountData.push(salesAmountHeaders);

    // 판매금액 데이터 추가
    sortedProductSalesByLocation.forEach((locationData) => {
      const row = [locationData.location.replace('특별자치시', '').replace('특별자치도', '')];
      let total = 0;

      sortedDates.forEach((date) => {
        const monthData = locationData.monthlyData.find((item) => item.date === date);
        const amount = monthData ? monthData.amount : 0;
        row.push(amount.toString());
        total += amount;
      });

      row.push(total.toString());
      salesAmountData.push(row);
    });

    // 판매건수 시트 추가
    const salesCountWorksheet = XLSX.utils.aoa_to_sheet(salesCountData);
    XLSX.utils.book_append_sheet(workbook, salesCountWorksheet, '지역별_월간_판매건수');

    // 판매금액 시트 추가
    const salesAmountWorksheet = XLSX.utils.aoa_to_sheet(salesAmountData);
    XLSX.utils.book_append_sheet(workbook, salesAmountWorksheet, '지역별_월간_판매금액');

    // 파일명 생성
    const fileName = `상품_지역별_월간_판매데이터_${moment(salesStartDate).format('YYYYMMDD')}_${moment(
      salesEndDate,
    ).format('YYYYMMDD')}.xlsx`;

    // 다운로드
    XLSX.writeFile(workbook, fileName);
  };

  const buyerRegionPaymentAmountsData = {
    labels: (statisticsStore.buyerRegionPaymentAmounts || []).slice(0, 30).map((item) => item._id),
    datasets: [
      {
        label: '결제 금액',
        data: (statisticsStore.buyerRegionPaymentAmounts || []).slice(0, 30).map((item) => item.totalAmount),
        borderWidth: 1,
        backgroundColor: BACKGROUND_COLORS,
      },
    ],
  };

  const dailyPaymentCountData = {
    labels: (statisticsStore.dailyPayments || []).map((item) => `${item._id}`),
    datasets: [
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyPayments || []).map((item) => item.class),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyPayments || []).map((item) => item.commerce),
      },
      {
        label: CHART_CATEGORY_LABELS.naver,
        borderColor: CHART_CATEGORY_COLORS.naver,
        backgroundColor: CHART_CATEGORY_COLORS.naver,
        data: (statisticsStore.dailyPayments || []).map((item) => item.naver),
      },
      {
        label: CHART_CATEGORY_LABELS.kakao,
        borderColor: CHART_CATEGORY_COLORS.kakao,
        backgroundColor: CHART_CATEGORY_COLORS.kakao,
        data: (statisticsStore.dailyPayments || []).map((item) => item.kakao),
      },
      {
        label: CHART_CATEGORY_LABELS.ezwel,
        borderColor: CHART_CATEGORY_COLORS.ezwel,
        backgroundColor: CHART_CATEGORY_COLORS.ezwel,
        data: (statisticsStore.dailyPayments || []).map((item) => item.ezwel || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyPayments || []).map(
          (item) => item.commerce + item.class + item.naver + item.kakao + (item.ezwel || 0),
        ),
      },
    ],
  };

  const dailyPurePaymentAmountData = {
    labels: (statisticsStore.dailyPayments || []).map((item) => `${item._id}`),
    datasets: [
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyPayments || []).map((paymentItem) => {
          const refundItem = (statisticsStore.dailyRefunds || []).find((refund) => {
            const formattedMonth = refund._id.month.toString().padStart(2, '0');
            const formattedDay = refund._id.day.toString().padStart(2, '0');
            const refundDate = `${refund._id.year}-${formattedMonth}-${formattedDay}`;
            return paymentItem._id === refundDate;
          });
          return paymentItem.classAmount - (refundItem?.classAmount || 0);
        }),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyPayments || []).map((paymentItem) => {
          const refundItem = (statisticsStore.dailyRefunds || []).find((refund) => {
            const formattedMonth = refund._id.month.toString().padStart(2, '0');
            const formattedDay = refund._id.day.toString().padStart(2, '0');
            const refundDate = `${refund._id.year}-${formattedMonth}-${formattedDay}`;
            return paymentItem._id === refundDate;
          });
          return paymentItem.commerceAmount - (refundItem?.commerceAmount || 0);
        }),
      },
      {
        label: CHART_CATEGORY_LABELS.naver,
        borderColor: CHART_CATEGORY_COLORS.naver,
        backgroundColor: CHART_CATEGORY_COLORS.naver,
        data: (statisticsStore.dailyPayments || []).map((paymentItem) => {
          const refundItem = (statisticsStore.dailyRefunds || []).find((refund) => {
            const formattedMonth = refund._id.month.toString().padStart(2, '0');
            const formattedDay = refund._id.day.toString().padStart(2, '0');
            const refundDate = `${refund._id.year}-${formattedMonth}-${formattedDay}`;
            return paymentItem._id === refundDate;
          });
          return paymentItem.naverAmount - (refundItem?.naverAmount || 0);
        }),
      },
      {
        label: CHART_CATEGORY_LABELS.kakao,
        borderColor: CHART_CATEGORY_COLORS.kakao,
        backgroundColor: CHART_CATEGORY_COLORS.kakao,
        data: (statisticsStore.dailyPayments || []).map((paymentItem) => {
          const refundItem = (statisticsStore.dailyRefunds || []).find((refund) => {
            const formattedMonth = refund._id.month.toString().padStart(2, '0');
            const formattedDay = refund._id.day.toString().padStart(2, '0');
            const refundDate = `${refund._id.year}-${formattedMonth}-${formattedDay}`;
            return paymentItem._id === refundDate;
          });
          return paymentItem.kakaoAmount - (refundItem?.kakaoAmount || 0);
        }),
      },
      {
        label: CHART_CATEGORY_LABELS.ezwel,
        borderColor: CHART_CATEGORY_COLORS.ezwel,
        backgroundColor: CHART_CATEGORY_COLORS.ezwel,
        data: (statisticsStore.dailyPayments || []).map((paymentItem) => {
          const refundItem = (statisticsStore.dailyRefunds || []).find((refund) => {
            const formattedMonth = refund._id.month.toString().padStart(2, '0');
            const formattedDay = refund._id.day.toString().padStart(2, '0');
            const refundDate = `${refund._id.year}-${formattedMonth}-${formattedDay}`;
            return paymentItem._id === refundDate;
          });
          return (paymentItem.ezwelAmount || 0) - (refundItem?.ezwelAmount || 0);
        }),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyPayments || []).map((paymentItem) => {
          const refundItem = (statisticsStore.dailyRefunds || []).find((refund) => {
            const formattedMonth = refund._id.month.toString().padStart(2, '0');
            const formattedDay = refund._id.day.toString().padStart(2, '0');
            const refundDate = `${refund._id.year}-${formattedMonth}-${formattedDay}`;
            return paymentItem._id === refundDate;
          });
          return (
            paymentItem.commerceAmount +
            paymentItem.classAmount +
            paymentItem.naverAmount +
            paymentItem.kakaoAmount +
            (paymentItem.ezwelAmount || 0) -
            ((refundItem?.commerceAmount || 0) +
              (refundItem?.classAmount || 0) +
              (refundItem?.naverAmount || 0) +
              (refundItem?.kakaoAmount || 0) +
              (refundItem?.ezwelAmount || 0))
          );
        }),
      },
    ],
  };

  const dailyPaymentAmountData = {
    labels: (statisticsStore.dailyPayments || []).map((item) => `${item._id}`),
    datasets: [
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyPayments || []).map((item) => item.classAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyPayments || []).map((item) => item.commerceAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.naver,
        borderColor: CHART_CATEGORY_COLORS.naver,
        backgroundColor: CHART_CATEGORY_COLORS.naver,
        data: (statisticsStore.dailyPayments || []).map((item) => item.naverAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.kakao,
        borderColor: CHART_CATEGORY_COLORS.kakao,
        backgroundColor: CHART_CATEGORY_COLORS.kakao,
        data: (statisticsStore.dailyPayments || []).map((item) => item.kakaoAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.ezwel,
        borderColor: CHART_CATEGORY_COLORS.ezwel,
        backgroundColor: CHART_CATEGORY_COLORS.ezwel,
        data: (statisticsStore.dailyPayments || []).map((item) => item.ezwelAmount || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyPayments || []).map(
          (item) =>
            item.commerceAmount + item.classAmount + item.naverAmount + item.kakaoAmount + (item.ezwelAmount || 0),
        ),
      },
    ],
  };

  const dailyPointUsageCountData = {
    labels: (statisticsStore.dailyPointUsage || []).map((item: DailyPointUsageCount) => `${item._id}`),
    datasets: [
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyPointUsage || []).map((item: DailyPointUsageCount) => item.class || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyPointUsage || []).map((item: DailyPointUsageCount) => item.commerce || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyPointUsage || []).map((item: DailyPointUsageCount) => item.class + item.commerce),
      },
    ],
  };

  const dailyPointUsageAmountData = {
    labels: (statisticsStore.dailyPointUsage || []).map((item: DailyPointUsageCount) => `${item._id}`),
    datasets: [
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyPointUsage || []).map((item: DailyPointUsageCount) => item.classPoint || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyPointUsage || []).map((item: DailyPointUsageCount) => item.commercePoint || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyPointUsage || []).map(
          (item: DailyPointUsageCount) => item.classPoint + item.commercePoint,
        ),
      },
    ],
  };

  const dailyRefundCountData = {
    labels: (statisticsStore.dailyRefunds || []).map((item) => `${item._id.year}-${item._id.month}-${item._id.day}`),
    datasets: [
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.class),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.commerce),
      },
      {
        label: CHART_CATEGORY_LABELS.naver,
        borderColor: CHART_CATEGORY_COLORS.naver,
        backgroundColor: CHART_CATEGORY_COLORS.naver,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.naver),
      },
      {
        label: CHART_CATEGORY_LABELS.kakao,
        borderColor: CHART_CATEGORY_COLORS.kakao,
        backgroundColor: CHART_CATEGORY_COLORS.kakao,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.kakao),
      },
      {
        label: CHART_CATEGORY_LABELS.ezwel,
        borderColor: CHART_CATEGORY_COLORS.ezwel,
        backgroundColor: CHART_CATEGORY_COLORS.ezwel,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.ezwel || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyRefunds || []).map(
          (item) => item.commerce + item.class + item.naver + item.kakao + (item.ezwel || 0),
        ),
      },
    ],
  };

  const dailyRefundAmountData = {
    labels: (statisticsStore.dailyRefunds || []).map((item) => `${item._id.year}-${item._id.month}-${item._id.day}`),
    datasets: [
      {
        label: CHART_CATEGORY_LABELS.class,
        borderColor: CHART_CATEGORY_COLORS.class,
        backgroundColor: CHART_CATEGORY_COLORS.class,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.classAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.commerce,
        borderColor: CHART_CATEGORY_COLORS.commerce,
        backgroundColor: CHART_CATEGORY_COLORS.commerce,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.commerceAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.naver,
        borderColor: CHART_CATEGORY_COLORS.naver,
        backgroundColor: CHART_CATEGORY_COLORS.naver,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.naverAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.kakao,
        borderColor: CHART_CATEGORY_COLORS.kakao,
        backgroundColor: CHART_CATEGORY_COLORS.kakao,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.kakaoAmount),
      },
      {
        label: CHART_CATEGORY_LABELS.ezwel,
        borderColor: CHART_CATEGORY_COLORS.ezwel,
        backgroundColor: CHART_CATEGORY_COLORS.ezwel,
        data: (statisticsStore.dailyRefunds || []).map((item) => item.ezwelAmount || 0),
      },
      {
        label: CHART_CATEGORY_LABELS.sum,
        borderColor: CHART_CATEGORY_COLORS.sum,
        backgroundColor: CHART_CATEGORY_COLORS.sum,
        data: (statisticsStore.dailyRefunds || []).map(
          (item) =>
            item.commerceAmount + item.classAmount + item.naverAmount + item.kakaoAmount + (item.ezwelAmount || 0),
        ),
      },
    ],
  };

  const purePaymentTotal =
    (statisticsStore.dailyPayments?.reduce(
      (acc, curr) =>
        acc + curr.classAmount + curr.commerceAmount + curr.naverAmount + curr.kakaoAmount + (curr.ezwelAmount || 0),
      0,
    ) || 0) -
    (statisticsStore.dailyRefunds?.reduce(
      (acc, curr) =>
        acc + curr.classAmount + curr.commerceAmount + curr.naverAmount + curr.kakaoAmount + (curr.ezwelAmount || 0),
      0,
    ) || 0);

  const paymentTotal = statisticsStore.dailyPayments?.reduce(
    (acc, curr) =>
      acc + curr.classAmount + curr.commerceAmount + curr.naverAmount + curr.kakaoAmount + (curr.ezwelAmount || 0),
    0,
  );

  const paymentCountTotal = statisticsStore.dailyPayments?.reduce(
    (acc, curr) => acc + curr.class + curr.commerce + curr.naver + curr.kakao + (curr.ezwel || 0),
    0,
  );

  const refundTotal = statisticsStore.dailyRefunds?.reduce(
    (acc, curr) =>
      acc + curr.classAmount + curr.commerceAmount + curr.naverAmount + curr.kakaoAmount + (curr.ezwelAmount || 0),
    0,
  );

  const dailyPointUsageTotal = statisticsStore.dailyPointUsage?.reduce(
    (acc, curr) => acc + curr.classPoint + curr.commercePoint,
    0,
  );

  const dailyNetSalesData = {
    labels: (statisticsStore.dailyNetSales || []).map((item: DailyNetSales) => `${item._id}`),
    datasets: [
      {
        label: '일별 순매출',
        borderColor: '#FF6B6B',
        backgroundColor: '#FFB6B6',
        data: (statisticsStore.dailyNetSales || []).map((item: DailyNetSales) => item.totalNetSales || 0),
      },
      {
        label: '챌린저스',
        borderColor: '#4CAF50',
        backgroundColor: '#A5D6A7',
        data: (statisticsStore.dailyNetSales || []).map(
          (item: DailyNetSales) => item.totalNetSalesForSpecialSeller || 0,
        ),
      },
      {
        label: '일반 파트너',
        borderColor: '#2196F3',
        backgroundColor: '#90CAF9',
        data: (statisticsStore.dailyNetSales || []).map((item: DailyNetSales) => item.totalNetSalesForOthers || 0),
      },
    ],
  };

  const dailyNetSalesTotal = statisticsStore.dailyNetSales?.reduce((acc, curr) => acc + curr.totalNetSales, 0) || 0;
  const dailyNetSalesTotalForSpecialSeller =
    statisticsStore.dailyNetSales?.reduce((acc, curr) => acc + curr.totalNetSalesForSpecialSeller, 0) || 0;
  const dailyNetSalesTotalForOthers =
    statisticsStore.dailyNetSales?.reduce((acc, curr) => acc + curr.totalNetSalesForOthers, 0) || 0;
  const dailyNetSalesPercent = (dailyNetSalesTotal / purePaymentTotal) * 100;
  const dailyNetSalesPercentForSpecialSeller = (dailyNetSalesTotalForSpecialSeller / purePaymentTotal) * 100;
  const dailyNetSalesPercentForOthers = (dailyNetSalesTotalForOthers / purePaymentTotal) * 100;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}>
        <div className="count-card">
          <div className="count-title">커머스 판매 파트너(기간)</div>
          {isLoadingPaymentCounts && <ClipLoader />}
          {!isLoadingPaymentCounts && (
            <div className="count-number">
              {`${statisticsStore.paymentCounts?.seller.hasIncome}`}
              <span
                className="count-suffix"
                style={{ color: sellerDiff > 0 ? 'green' : 'red' }}>{`${sellerDiff}`}</span>
              {` / ${statisticsStore.paymentCounts?.seller.total}`}
            </div>
          )}
        </div>
        <div className="count-card">
          <div className="count-title">커머스 판매 상품(기간)</div>
          {isLoadingPaymentCounts && <ClipLoader />}
          {!isLoadingPaymentCounts && (
            <div className="count-number">
              {`${statisticsStore.paymentCounts?.commerce}`}
              <span
                className="count-suffix"
                style={{ color: commerceDiff > 0 ? 'green' : 'red' }}>{`${commerceDiff}`}</span>
              {` / ${statisticsStore.productCounts?.commerce.open}`}
            </div>
          )}
        </div>
        <div className="count-card">
          <div className="count-title">클래스 판매 파트너(기간)</div>
          {isLoadingPaymentCounts && <ClipLoader />}
          {!isLoadingPaymentCounts && (
            <div className="count-number">
              {`${statisticsStore.paymentCounts?.tutor.hasIncome}`}
              <span className="count-suffix" style={{ color: tutorDiff > 0 ? 'green' : 'red' }}>{`${tutorDiff}`}</span>
              {` / ${statisticsStore.paymentCounts?.tutor.total}`}
            </div>
          )}
        </div>
        <div className="count-card">
          <div className="count-title">클래스 판매 상품</div>
          {isLoadingPaymentCounts && <ClipLoader />}
          {!isLoadingPaymentCounts && (
            <div className="count-number">
              {`${statisticsStore.paymentCounts?.class}`}
              <span className="count-suffix" style={{ color: classDiff > 0 ? 'green' : 'red' }}>{`${classDiff}`}</span>
              {` / ${statisticsStore.productCounts?.class.open}`}
            </div>
          )}
        </div>
        <div className="count-card" style={{ width: '240px' }}>
          <div className="count-title">전체 미사용 포인트</div>
          {isLoadingTotalUnusedCreditPoints && <ClipLoader />}
          {!isLoadingTotalUnusedCreditPoints && (
            <div className="count-number">
              {totalUnusedCreditPoints?.data?.totalCreditPoints?.toLocaleString() || '0'}
              <span className="count-suffix" style={{ color: '#666' }}>
                P
              </span>
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {/* 시간별 누적 판매 금액 차트 - 3배 넓이 */}
        <Card
          className="cumulative-sales-chart"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>시간별 누적 판매 금액 ({moment(cumulativeSalesBaseDate).format('YYYY-MM-DD')} vs 7일 전)</span>
              <DatePicker
                value={dayjs(cumulativeSalesBaseDate)}
                onChange={(date) => {
                  if (date) {
                    setCumulativeSalesBaseDate(date.format('YYYY-MM-DD'));
                  }
                }}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </div>
          }
          bordered={false}>
          {isLoadingCumulativeHourlySales && <ClipLoader />}
          {!isLoadingCumulativeHourlySales && statisticsStore.cumulativeHourlySales && (
            <CumulativeSalesChart data={statisticsStore.cumulativeHourlySales} />
          )}
        </Card>
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <Card
          className="chart"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>일별 결제 수</div>
              <div>
                <span>총 결제 수: {paymentCountTotal?.toLocaleString()}건</span>
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyPayments && <ClipLoader />}
          {!isLoadingDailyPayments && <Line data={dailyPaymentCountData} />}
        </Card>
        <Card className="chart" title="일별 포인트 사용 수" bordered={false}>
          {isLoadingDailyPointUsage && <ClipLoader />}
          {!isLoadingDailyPointUsage && <Line data={dailyPointUsageCountData} />}
        </Card>
        <Card className="chart" title="일별 환불 수" bordered={false}>
          {isLoadingDailyRefunds && <ClipLoader />}
          {!isLoadingDailyRefunds && <Line data={dailyRefundCountData} />}
        </Card>
        <Card
          className="chart"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>일별 결제 금액</div>
              <div>
                <span>총 결제 금액: {paymentTotal?.toLocaleString()}원</span>
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyPayments && <ClipLoader />}
          {!isLoadingDailyPayments && <Line data={dailyPaymentAmountData} />}
        </Card>
        <Card
          className="chart"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>일별 환불 금액</div>
              <div>총 환불 금액: {refundTotal?.toLocaleString()}원</div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyRefunds && <ClipLoader />}
          {!isLoadingDailyRefunds && <Line data={dailyRefundAmountData} />}
        </Card>
        <Card
          className="chart"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>일별 순결제 금액</div>
              <div>총 결제 금액: {purePaymentTotal.toLocaleString()}원</div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyPayments && <ClipLoader />}
          {!isLoadingDailyPayments && <Line data={dailyPurePaymentAmountData} />}
        </Card>
        <Card
          className="chart"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>일별 포인트 사용 금액</div>
              <div>총 포인트 사용 금액: {dailyPointUsageTotal?.toLocaleString()}원</div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyPointUsage && <ClipLoader />}
          {!isLoadingDailyPointUsage && <Line data={dailyPointUsageAmountData} />}
        </Card>
        <Card
          className="chart"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tooltip
                overlay={
                  <div>
                    <div>{`일반 파트너: 결제 금액 + 환불건 결제 금액 - 포인트 사용 금액 - 환불 금액 - 정산예정금액`}</div>
                    <div>{`챌린저스: 결제 금액 + 환불건 결제 금액 - 포인트 사용 금액 - 환불 금액`}</div>
                  </div>
                }>
                <div>일별 순매출</div>
              </Tooltip>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Tooltip
                  overlay={
                    <div>
                      <div>
                        {`일반 파트너: ${dailyNetSalesTotalForOthers?.toLocaleString()}원`}
                        <span>({dailyNetSalesPercentForOthers.toFixed(2)}%)</span>
                      </div>
                      <div>
                        {`챌린저스: ${dailyNetSalesTotalForSpecialSeller?.toLocaleString()}원`}
                        <span>({dailyNetSalesPercentForSpecialSeller.toFixed(2)}%)</span>
                      </div>
                    </div>
                  }>
                  <div> 총 순매출: {dailyNetSalesTotal?.toLocaleString()}원 </div>
                </Tooltip>
                <Tooltip title={'일별 순결제 금액 대비 순매출 비율'}>
                  <div>({dailyNetSalesPercent.toFixed(2)}%)</div>
                </Tooltip>
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingDailyNetSales && <ClipLoader />}
          {!isLoadingDailyNetSales && <Line data={dailyNetSalesData} />}
        </Card>
        <Card
          className="chart"
          style={{ height: '352px' }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>포인트 보유 상위 50명</div>
              {creditPointsStatistics?.data?.pointRangeStats && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <span>5만P 이상: {creditPointsStatistics.data.pointRangeStats.above50000}명</span>
                  <span style={{ marginLeft: '8px' }}>
                    3만P 이상: {creditPointsStatistics.data.pointRangeStats.above30000}명
                  </span>
                  <span style={{ marginLeft: '8px' }}>
                    1만P 이상: {creditPointsStatistics.data.pointRangeStats.above10000}명
                  </span>
                </div>
              )}
            </div>
          }
          bordered={false}>
          {isLoadingCreditPointsStatistics && <ClipLoader />}
          {!isLoadingCreditPointsStatistics && (
            <div style={{ height: '200px' }}>
              <Table
                columns={creditPointsColumns}
                dataSource={creditPointsStatistics?.data?.topUsers || []}
                pagination={false}
                size="small"
                scroll={{ y: 200 }}
                rowKey="_id"
              />
            </div>
          )}
        </Card>
        <MonthlyPaymentComparisonChart
          data={statisticsStore.monthlyPaymentComparison || []}
          isLoading={isLoadingMonthlyPaymentComparison}
        />
        <PaymentTimeSlotTrendsChart
          data={statisticsStore.paymentTimeSlotTrends || []}
          isLoading={isLoadingPaymentTimeSlotTrends}
        />
        <Card
          className="chart"
          style={{ maxWidth: 'calc(50% - 6px)', minWidth: 'calc(50% - 6px)' }}
          title={`구매자 지역별 결제 금액 TOP 30 (${moment(statisticsStore.startDate).format('MM/DD')} - ${moment(
            statisticsStore.endDate,
          ).format('MM/DD')})`}
          bordered={false}>
          {isLoadingBuyerRegionPaymentAmounts && <ClipLoader />}
          {!isLoadingBuyerRegionPaymentAmounts && <Bar data={buyerRegionPaymentAmountsData} />}
        </Card>
        <Card
          className="chart"
          style={{ maxWidth: '100%', minWidth: 'calc(50% - 6px)' }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>상품 지역별 월간 판매건수</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size="small"
                  onClick={downloadProductSalesByLocationExcel}
                  disabled={!productSalesByLocation?.data || isLoadingProductSalesByLocation}>
                  엑셀 다운로드
                </Button>
                <DatePicker
                  value={dayjs(salesStartDate)}
                  onChange={(date) => setSalesStartDate(date ? date.format('YYYY-MM-DD') : '')}
                  format="YYYY-MM-DD"
                  placeholder="시작일"
                  size="small"
                />
                <span>~</span>
                <DatePicker
                  value={dayjs(salesEndDate)}
                  onChange={(date) => setSalesEndDate(date ? date.format('YYYY-MM-DD') : '')}
                  format="YYYY-MM-DD"
                  placeholder="종료일"
                  size="small"
                />
              </div>
            </div>
          }
          bordered={false}>
          {isLoadingProductSalesByLocation && <ClipLoader />}
          {!isLoadingProductSalesByLocation && (
            <Line
              data={productSalesByLocationData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
              }}
              style={{ width: '100%', height: '400px' }}
              width={undefined}
              height={400}
            />
          )}
        </Card>
        <Card className="donut-chart" title="현재 판매중인 지역별 상품 수" bordered={false}>
          {isLoadingProductCountsByLocation && <ClipLoader />}
          {!isLoadingProductCountsByLocation && <Doughnut data={productCountsByLocationData} />}
        </Card>

        <Card className="donut-chart" title="커머스 파트너 TOP 10" bordered={false}>
          {isLoadingPaymentRankCommerceTop && <ClipLoader />}
          {!isLoadingPaymentRankCommerceTop && <Doughnut data={paymentRankCommerceTop} />}
        </Card>
        <Card className="donut-chart" title="클래스 파트너 TOP 10" bordered={false}>
          {isLoadingPaymentRankClassTop && <ClipLoader />}
          {!isLoadingPaymentRankClassTop && <Doughnut data={paymentRankClassTop} />}
        </Card>
        <Card className="donut-chart" title="네이버 TOP 10" bordered={false}>
          {isLoadingPaymentRankNaverTop && <ClipLoader />}
          {!isLoadingPaymentRankNaverTop && <Doughnut data={paymentRankNaverTop} />}
        </Card>
        <Card className="donut-chart" title="카카오 TOP 10" bordered={false}>
          {isLoadingPaymentRankKakaoTop && <ClipLoader />}
          {!isLoadingPaymentRankKakaoTop && <Doughnut data={paymentRankKakaoTop} />}
        </Card>
        <Card className="donut-chart" title="이지웰 TOP 10" bordered={false}>
          {isLoadingPaymentRankEzwelTop && <ClipLoader />}
          {!isLoadingPaymentRankEzwelTop && <Doughnut data={paymentRankEzwelTop} />}
        </Card>
        <Card className="donut-chart" title="환불 판매자 TOP 20" bordered={false}>
          {isLoadinRefundRankSeller && <ClipLoader />}
          {!isLoadinRefundRankSeller && <Doughnut data={refundRankSeller} />}
        </Card>
        <Card className="donut-chart" title="환불 상품 TOP 20" bordered={false}>
          {isLoadinRefundRankProduct && <ClipLoader />}
          {!isLoadinRefundRankProduct && <Doughnut data={refundRankProduct} />}
        </Card>
      </div>
    </div>
  );
};

export default PaymentAnalysisTab;
